import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, Globe, Search } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Input } from '../ui/input';

/**
 * M4-A — the single-market picker.
 *
 * One compact control in place of the permanently visible country grid. Closed, it shows the
 * chosen market or "Select market"; open, it is a searchable list. Exactly ONE market can be
 * chosen: `value` is a market name or null, and `onChange` receives the name picked. The
 * caller keeps its own storage shape — the Dashboard still holds `countries`, now `[]` or
 * `[market]` — so the search request body and the sub-niche editor are untouched.
 *
 * Display only. The catalogue arrives through `options`; nothing here knows which markets
 * the engine serves, or in which language. The backend stays the authority on that.
 *
 * Filtering is a case-insensitive substring match on the trimmed query. The query is cleared
 * whenever the popover OPENS, so every opening starts from the full list while a closing
 * panel keeps showing exactly what it showed.
 *
 * The trigger carries `role="combobox"`, which is also what gives it the World input surface
 * and the violet focus glow inside a `world-card` (see styles/world-theme.css); `world-input`
 * is added as well so it looks the same outside a card.
 */
export const MarketPicker = ({
  options,
  value,
  onChange,
  placeholder = 'Select market',
  searchPlaceholder = 'Search market...',
  emptyText = 'No markets found',
  ariaLabel = 'Target market',
  id,
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const listId = useId();

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return options;
    return options.filter((market) => market.toLowerCase().includes(needle));
  }, [options, query]);

  // The search is cleared on OPEN, never on close: Radix keeps the panel mounted while it fades
  // out, and clearing on close re-rendered the whole list in those frames. Both updates land in
  // one render, so an opening never shows the previous search either.
  const handleOpenChange = (next) => {
    if (next) setQuery('');
    setOpen(next);
  };

  const choose = (market) => {
    onChange(market);
    handleOpenChange(false);
  };

  // Opened with a market already chosen: bring it into view. Guarded, because jsdom has no
  // scrollIntoView and a missing method must never break the picker.
  useEffect(() => {
    if (!open) return;
    const selected = listRef.current?.querySelector('[aria-selected="true"]');
    selected?.scrollIntoView?.({ block: 'nearest' });
  }, [open]);

  const optionButtons = () =>
    Array.from(listRef.current?.querySelectorAll('[role="option"]') || []);

  const focusOption = (index) => {
    const buttons = optionButtons();
    if (buttons.length === 0) return;
    const clamped = Math.max(0, Math.min(index, buttons.length - 1));
    buttons[clamped].focus();
  };

  const handleInputKeyDown = (event) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      focusOption(0);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      // Enter commits a typed search — the first match — and only when something was typed.
      // With an empty query nothing was searched for, and the list is browsed with the
      // arrow keys instead.
      if (query.trim() && filtered.length > 0) choose(filtered[0]);
    }
  };

  const handleOptionKeyDown = (event, index) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      focusOption(index + 1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (index === 0) inputRef.current?.focus();
      else focusOption(index - 1);
    } else if (event.key === 'Home') {
      event.preventDefault();
      focusOption(0);
    } else if (event.key === 'End') {
      event.preventDefault();
      focusOption(filtered.length - 1);
    }
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          id={id}
          role="combobox"
          aria-label={ariaLabel}
          aria-haspopup="listbox"
          aria-expanded={open}
          disabled={disabled}
          className="world-input flex h-11 w-full items-center gap-2 rounded-md px-3 text-sm focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
          data-testid="market-picker-trigger"
        >
          <Globe className="h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" />
          <span
            className={`flex-1 truncate text-left ${value ? 'text-slate-100' : 'text-slate-400'}`}
            data-testid="market-picker-value"
          >
            {value || placeholder}
          </span>
          <ChevronDown
            className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`}
            aria-hidden="true"
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={6}
        className="w-[var(--radix-popover-trigger-width)] overflow-hidden p-0"
        onOpenAutoFocus={(event) => {
          // Focus lands in the search box, not on the panel, so typing filters at once.
          event.preventDefault();
          inputRef.current?.focus();
        }}
        data-testid="market-picker-content"
      >
        <div className="border-b border-white/10 p-2">
          <div className="relative">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
              aria-hidden="true"
            />
            <Input
              ref={inputRef}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={handleInputKeyDown}
              placeholder={searchPlaceholder}
              aria-label="Search markets"
              aria-controls={listId}
              autoComplete="off"
              className="world-input h-10 pl-9"
              data-testid="market-search-input"
            />
          </div>
        </div>
        <div
          ref={listRef}
          id={listId}
          role="listbox"
          aria-label={ariaLabel}
          className="custom-scrollbar max-h-64 overflow-y-auto p-1"
          data-testid="market-picker-list"
        >
          {filtered.length === 0 ? (
            <p
              className="px-3 py-6 text-center text-sm text-slate-400"
              data-testid="market-picker-empty"
            >
              {emptyText}
            </p>
          ) : (
            filtered.map((market, index) => {
              const selected = market === value;
              return (
                <button
                  key={market}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  onClick={() => choose(market)}
                  onKeyDown={(event) => handleOptionKeyDown(event, index)}
                  className={`flex min-h-[44px] w-full items-center justify-between gap-2 rounded-md px-3 py-2 text-left text-sm transition-colors hover:bg-white/10 focus:outline-none focus-visible:bg-white/10 ${
                    selected ? 'text-blue-300' : 'text-slate-200'
                  }`}
                  data-testid={`market-option-${market}`}
                >
                  <span className="truncate">{market}</span>
                  {selected && (
                    <Check className="h-4 w-4 shrink-0 text-blue-400" aria-hidden="true" />
                  )}
                </button>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default MarketPicker;
